import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bell, Check, Search } from "lucide-react";

function timeAgo(iso) {
    try {
        const d = new Date(iso);
        const s = Math.floor((Date.now() - d.getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch { return ""; }
}

export default function NotificationsBell() {
    const { notifications, unreadCount, loadNotifications, markNotificationRead, markAllNotificationsRead } = useAuth();
    const [open, setOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);
    const [panelTop, setPanelTop] = useState(0);
    const navigate = useNavigate();
    const ref = useRef(null);

    useEffect(() => {
        if (open) loadNotifications();
    }, [open, loadNotifications]);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const toggle = () => {
        if (!open && ref.current) setPanelTop(ref.current.getBoundingClientRect().bottom + 8);
        setOpen((v) => !v);
    };

    const onItem = async (n) => {
        if (!n.read) await markNotificationRead(n.id);
        setOpen(false);
        if (n.link) navigate(n.link);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                data-testid="notifications-bell"
                onClick={toggle}
                className="relative text-sm uppercase tracking-wider text-white/80 hover:text-[#ff4500] transition-colors px-3 py-2 inline-flex items-center gap-1.5"
                title="Notifications"
            >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                    <span
                        data-testid="notifications-badge"
                        className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-1 bg-[#ff4500] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    >
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </button>
            {open && (
                <div
                    data-testid="notifications-panel"
                    style={isMobile ? { position: "fixed", top: panelTop, left: 8, right: 8 } : undefined}
                    className={`${isMobile ? "" : "absolute right-0 top-full mt-2 w-[340px]"} max-h-[70vh] overflow-y-auto bg-[#141414] border border-[#2a2a2a] shadow-2xl z-[200]`}
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] sticky top-0 bg-[#141414]">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Notifications</div>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                data-testid="mark-all-read"
                                onClick={markAllNotificationsRead}
                                className="text-[10px] uppercase tracking-[0.15em] text-[#ff4500] hover:underline inline-flex items-center gap-1"
                            >
                                <Check className="h-3 w-3" /> Mark all read
                            </button>
                        )}
                    </div>
                    {notifications.length === 0 ? (
                        <div className="px-4 py-10 text-center text-sm text-[#a0a0a0]" data-testid="notifications-empty">
                            No notifications yet
                        </div>
                    ) : (
                        notifications.map((n) => (
                            <button
                                key={n.id}
                                type="button"
                                data-testid={`notification-${n.id}`}
                                onClick={() => onItem(n)}
                                className={`w-full text-left px-4 py-3 border-b border-[#2a2a2a] hover:bg-[#0f0f0f] transition-colors flex gap-3 ${n.read ? "opacity-60" : ""}`}
                            >
                                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#ff4500] shrink-0" />}
                                <span className={`min-w-0 flex-1 ${n.read ? "pl-0" : ""}`}>
                                    <span className="block text-sm text-white leading-tight">{n.title}</span>
                                    {n.body && <span className="block text-xs text-[#a0a0a0] truncate mt-0.5">{n.body}</span>}
                                    <span className="block text-[10px] uppercase tracking-[0.15em] text-[#666] mt-1">{timeAgo(n.created_at)}</span>
                                </span>
                            </button>
                        ))
                    )}
                    <Link
                        to="/notifications"
                        data-testid="nav-notification-center"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 border-t border-[#2a2a2a] text-[11px] uppercase tracking-[0.15em] text-white hover:text-[#ff4500] hover:bg-[#0f0f0f] transition-colors"
                    >
                        <Bell className="h-3.5 w-3.5" /> View all notifications
                    </Link>
                    <Link
                        to="/alerts"
                        data-testid="nav-alerts"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 border-t border-[#2a2a2a] text-[11px] uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-[#ff4500] hover:bg-[#0f0f0f] transition-colors sticky bottom-0 bg-[#141414]"
                    >
                        <Search className="h-3.5 w-3.5" /> Saved-search alerts
                    </Link>
                </div>
            )}
        </div>
    );
}
